import { useCallback, useMemo } from "react";
import { useFetcher } from "~/hooks/useFetcher";
import { useUserPreferences, type ConnectorPrefsEntry } from "~/hooks/useUserPreferences";
import { agentConnectorsPath, type AgentConnector } from "~/services/agents";
import { trackEvent } from "~/utils/monitoring";
import { useMcpGuidelines, type McpGuidelines } from "./useMcpGuidelines";

/**
 * Resolves which connector names are enabled, given the agent's granted set
 * and any saved preferences for this context.
 *
 * Connectors default OFF: a fresh context enables nothing, and the user opts
 * in explicitly per connector. The selection is the user's saved choices
 * intersected with the names still granted to the agent.
 */
export function resolveSelectedConnectors(
  granted: AgentConnector[],
  saved: ConnectorPrefsEntry | null
): string[] {
  if (!saved) {
    return [];
  }
  const selected = new Set(saved.selected);
  return granted.map((g) => g.connector.name).filter((name) => selected.has(name));
}

interface UseConnectorsResult {
  /** Connectors granted to the conversation's agent. Empty while loading or
   * for conversations with no bound agent. */
  connectors: AgentConnector[];
  /** Connector names currently enabled for this context (subset of granted). */
  selectedConnectorNames: string[];
  /** Toggle a single connector by name; persists the result to preferences. */
  toggleConnector: (connectorName: string) => void;
  /** The per-turn connector allow-list to forward to the agent. Empty until the
   * user accepts the MCP guidelines (the compliance gate) and `undefined` once
   * accepted but before the granted set has loaded successfully — so the backend
   * falls back to its "use every granted connector" default rather than being
   * handed a premature empty list. */
  forwardedConnectors: string[] | undefined;
  /** MCP-guidelines acceptance state and accept action for the picker banner. */
  mcpGuidelines: McpGuidelines;
  isLoading: boolean;
}

/**
 * Fetches `GET /api/conversations/v1/agents/{agentId}/connectors` and manages a
 * per-context selection of connector names, persisted in localStorage so it
 * survives a refresh. The selection is sent as `forwardedProps.connectors` on
 * each run — the per-turn allow-list the analysis-agent filters against.
 *
 * `contextId` is keyed by project and agent so toggles persist across the
 * conversations a project shares with the same agent.
 */
export function useConnectors(
  agentId: string | null | undefined,
  projectId: string | null | undefined
): UseConnectorsResult {
  const fetchKey = agentId ? agentConnectorsPath(agentId) : null;
  const { data, isLoading } = useFetcher<AgentConnector[]>(fetchKey);
  const connectors = useMemo(() => data ?? [], [data]);
  // A settled-but-failed fetch leaves `data` undefined with `isLoading` false,
  // so gate the allow-list on a successful fetch rather than on `!isLoading`.
  // Otherwise an error would forward an empty list — disabling every connector
  // — instead of omitting the field and letting the backend use all grants.
  const loaded = data !== undefined;

  const contextId = agentId ? `${projectId ?? ""}:${agentId}` : "";

  const mcpGuidelines = useMcpGuidelines();
  const [preferences, updatePreferences] = useUserPreferences();

  const selectedConnectorNames = useMemo(() => {
    if (connectors.length === 0) return [];
    const saved = contextId ? (preferences.connectorPreferences?.[contextId] ?? null) : null;
    return resolveSelectedConnectors(connectors, saved);
  }, [connectors, preferences.connectorPreferences, contextId]);

  const toggleConnector = useCallback(
    (connectorName: string) => {
      if (!contextId) return;
      const isEnabled = selectedConnectorNames.includes(connectorName);
      const newSelection = isEnabled
        ? selectedConnectorNames.filter((n) => n !== connectorName)
        : [...selectedConnectorNames, connectorName];

      trackEvent("nextgen_connector_toggled", {
        context_id: contextId,
        connector_name: connectorName,
        enabled: !isEnabled,
        total_selected: newSelection.length,
      });

      updatePreferences({
        connectorPreferences: {
          ...preferences.connectorPreferences,
          [contextId]: { selected: newSelection },
        },
      });
    },
    [selectedConnectorNames, contextId, preferences.connectorPreferences, updatePreferences]
  );

  // Compliance gate: forward no connectors until the user accepts the MCP
  // guidelines, regardless of any saved selection or grant-fetch state. Once
  // accepted, defer to the backend default while the granted set is still
  // loading (or failed), then forward the user's explicit selection.
  const forwardedConnectors = !agentId
    ? undefined
    : mcpGuidelines.accepted !== true
      ? []
      : loaded
        ? selectedConnectorNames
        : undefined;

  return {
    connectors,
    selectedConnectorNames,
    toggleConnector,
    forwardedConnectors,
    mcpGuidelines,
    isLoading,
  };
}
